import * as angular from 'angular';
import * as _ from 'lodash';
import template from './editSectionChange.tpl.html';
import {Strategy, StrategyConfig} from "./sectionChange";
import {Plant} from "../../models/navigation";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import UIUtilityServiceName, {EnumOption, UIUtilityService} from "../../services/utility/uiUtility.service";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import NotifyServiceName, {NotifyService} from "../../services/notify/notify.service";
import SectionChangeServiceName, {SectionChangeService} from "../../services/toolChange/sectionChange.service";
import NewGuidServiceName, {NewGuidService} from "../../services/utility/newguid.service";
import {apsEnums} from "../../shared/dto/aps.enums";

let requires = [UIUtilityServiceName, LoaderServiceName, NotifyServiceName, SectionChangeServiceName,
  NewGuidServiceName];

export interface SectionChangeEditParams {
  strategy: Strategy;
  plant: Plant;
  shop: apsViewModels.ShopNavigation;
  canBeDefault: boolean;
  readOnly: boolean;
  tools: apsViewModels.ToolAndType[];
}

export function openEditSectionChange(data: SectionChangeEditParams,
                                      $modal: mm.foundation.modal): angular.IPromise<Strategy> {
  return $modal.open({
    template: template,
    controller: 'SectionChangeEditCtrl as vm',
    resolve: {
      params: function() {
        return data;
      }
    }
  }).result;
}

interface SectionChangeEditController {
  strategy: Strategy;
  plant: Plant;
  shop: apsViewModels.ShopNavigation;
  canBeDefault: boolean;
  readOnly: boolean;
  orders: EnumOption[];
  sections: EnumOption[];
  tools: apsViewModels.ToolAndType[];
  form: angular.IFormController;

  submit(): void;
  cancel(): void;
  disableShift(config: StrategyConfig, shift: number): boolean;
  addShiftGroup(): void;
  canAddShiftGroup(): boolean;
  canRemoveShiftGroup(): boolean;
  removeShiftGroup(config: StrategyConfig): void;
  addTool(config: StrategyConfig): void;
  removeTool(config: StrategyConfig, detail: apsViewModels.SectionChangeConfigDetailData): void;
  canRemoveTool(config: StrategyConfig): boolean;

  getTools(detail: apsViewModels.SectionChangeConfigDetailData): apsViewModels.ToolAndType[];

  shiftsRequired(): boolean;
  shiftRequired(config: StrategyConfig): boolean;
  toolsRequired(config: StrategyConfig): boolean;
}

class SectionChangeEditCtrl implements SectionChangeEditController {
  form: angular.IFormController;
  strategy: Strategy;
  plant: Plant;
  shop: apsViewModels.ShopNavigation;
  canBeDefault: boolean;
  readOnly: boolean;
  orders: EnumOption[];
  sections: EnumOption[];
  tools: apsViewModels.ToolAndType[];

  private loaderService: LoaderService;
  private notifyService: NotifyService;
  private sectionChangeService: SectionChangeService;
  private newGuidService: NewGuidService;
  private $modalInstance: mm.foundation.modalInstance;
  private uiUtilityService: UIUtilityService;

  constructor(
    params: SectionChangeEditParams,
    loaderService: LoaderService,
    notifyService: NotifyService,
    sectionChangeService: SectionChangeService,
    newGuidService: NewGuidService,
    uiUtilityService: UIUtilityService,
    $modalInstance: mm.foundation.modalInstance
  ) {
    this.loaderService = loaderService;
    this.notifyService = notifyService;
    this.sectionChangeService = sectionChangeService;
    this.newGuidService = newGuidService;
    this.uiUtilityService = uiUtilityService;
    this.$modalInstance = $modalInstance;

    this.plant = params.plant;
    this.shop = params.shop;
    this.canBeDefault = params.canBeDefault;

    this.tools = params.tools;
    this.orders = uiUtilityService.getListOptionsFromEnum(apsEnums.SectionChangeOrder, 1);
    this.sections = uiUtilityService.getListOptionsFromEnum(apsEnums.SectionChangeSections, 1);

    if (params.strategy) {
      this.strategy = JSON.parse(JSON.stringify(params.strategy));
    } else {
      this.strategy = {
        id: null,
        isDefault: false,
        name: '',
        plantId: this.plant.id,
        plantName: this.plant.name,
        shopId: this.shop.id,
        shopName: this.shop.name,
        canEdit: true,
        configs: []
      };
      this.addShiftGroup();
      this.newGuidService
        .getNewGuid('sectionChangeStrategy')
        .then(id => this.strategy.id = id);
    }
  }

  shiftsInvalid(): boolean {
    return !this.strategy.id || this.shiftsRequired()
      || _.some(this.strategy.configs, c => !c.id
        || this.toolsRequired(c)
        || this.shiftRequired(c)
        || _.some(c.details, d => !d.id));
  }

  shiftsRequired(): boolean {
    return this.form.$submitted
      && this.strategy.configs.length === 0;
  }

  toolsRequired(config: StrategyConfig): boolean {
    return this.form.$submitted
      && config.details.length === 0;
  }

  shiftRequired(config: StrategyConfig): boolean {
    return this.form.$submitted
      && !(config.shift1 || config.shift2 || config.shift3);
  }

  submit(): void {
    // this.form.$setSubmitted();

    if (this.form.$invalid
      || this.shiftsInvalid()) {
      return;
    }

    _.forEach(this.strategy.configs, c => {
      /* tslint:disable:no-bitwise */
      c.shifts = c.shift1 ? apsEnums.Shift.First : 0
        | (c.shift2 ? apsEnums.Shift.Second : 0)
        | (c.shift3 ? apsEnums.Shift.Third : 0);
      /* tslint:enable:no-bitwise */

      _.forEach(c.details, d => {
        if (d.sections !== apsEnums.SectionChangeSections.Number) {
          d.numberOfSections = null;
        }
      });
    });

    this.sectionChangeService.addUpdateStrategy(this.strategy)
      .then(() => {
        this.$modalInstance.close(this.strategy);
      }, this.notifyService.handleApiErrors);
  }

  cancel(): void {
    this.$modalInstance.close();
  }

  disableShift(config: StrategyConfig, shift: apsEnums.Shift): boolean {
    if (shift === 1) {
      if (config.shift1) {
        return false;
      }
      return _.some(this.strategy.configs, c => c.shift1);
    }
    if (shift === 2) {
      if (config.shift2) {
        return false;
      }
      return _.some(this.strategy.configs, c => c.shift2);
    }
    if (config.shift3) {
      return false;
    }
    return _.some(this.strategy.configs, c => c.shift3);
  }

  addShiftGroup(): void {
    let config = {
      shift1: false,
      shift2: false,
      shift3: false,
      id: null,
      shifts: 0,
      runOnHolidays: false,
      runOnWeekends: false,
      delayDays: 0,
      details: []
    };
    this.addTool(config);
    this.strategy.configs.push(config);
    this.newGuidService.getNewGuid('sectionChangeConfig').then(id => config.id = id);
  }

  canAddShiftGroup(): boolean {
    return this.strategy.configs.length === 0
      || _.every(this.strategy.configs, c => !c.shift1)
      || _.every(this.strategy.configs, c => !c.shift2)
      || _.every(this.strategy.configs, c => !c.shift3);
  }

  removeShiftGroup(config: StrategyConfig): void {
    _.remove(this.strategy.configs, config);
  }

  addTool(config: StrategyConfig): void {
    let detail = {
      id: null,
      toolId: null,
      sections: apsEnums.SectionChangeSections.Number,
      numberOfSections: 1,
      order: apsEnums.SectionChangeOrder.Sequential
    };
    config.details.push(detail);
    this.newGuidService.getNewGuid('sectionChangeConfigDetail').then(id => detail.id = id);
  }

  removeTool(config: StrategyConfig,
             detail: apsViewModels.SectionChangeConfigDetailData): void {
    _.remove(config.details, detail);
  }

  getTools(detail: apsViewModels.SectionChangeConfigDetailData): apsViewModels.ToolAndType[] {
    let toolIds = _.map(_.xor(_.flatten(
      _.map(this.strategy.configs, 'details')), [detail]), 'toolId');
    return _.filter(this.tools, t => _.every(toolIds, id => id !== t.id));
  }

  canRemoveShiftGroup(): boolean {
    return this.strategy.configs.length > 1;
  }

  canRemoveTool(config: StrategyConfig): boolean {
    return config.details.length > 1;
  }
}

export default angular.module('aps.controller.editSectionChange', requires)
  .controller('SectionChangeEditCtrl', SectionChangeEditCtrl)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/sectionChange/editSectionChange.ts